import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useEvent } from "../lib/event";
import DashboardLayout from "../components/DashboardLayout";
import { notify } from "../lib/notify";
import { Maximize2, Search, Ticket as TicketIcon, CheckCircle2, AlertTriangle, XCircle, Ban, Clock, CalendarDays, ScanLine } from "lucide-react";
import { promptDialog, alertDialog, confirmDialog } from "../components/ConfirmDialog";
import CompactCalendar, { pickSmartDefault } from "../components/CompactCalendar";
import { formatOrdinalDate } from "../lib/timeFormat";

const REASON_MSG = {
  not_found: "Ticket not found.",
  wrong_attraction: "This ticket belongs to a different attraction.",
  ticket_cancelled: "This ticket has been cancelled.",
  order_not_confirmed: "Order is not confirmed / paid.",
};

async function doCheckin(id, { device = "web-admin", force = false } = {}) {
  const r = await api.post("/checkin", { attendee_id: id, device, force });
  return r.data;
}

export default function CheckinPage() {
  const { currentEventId } = useEvent();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [eventDates, setEventDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);   // 'YYYY-MM-DD'
  const [dateResults, setDateResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingDate, setLoadingDate] = useState(false);

  // Load the event's dates for the calendar; default to the closest date.
  useEffect(() => {
    (async () => {
      if (!currentEventId) { setEventDates([]); setSelectedDate(null); return; }
      try {
        const r = await api.get(`/events/${currentEventId}`);
        const dates = (r.data.dates || []).filter((d) => d.status !== "cancelled" && !d.is_blocked);
        setEventDates(dates);
        setSelectedDate(pickSmartDefault(dates)?.date || null);
      } catch { setEventDates([]); setSelectedDate(null); }
    })();
  }, [currentEventId]);

  const loadDate = async () => {
    if (!selectedDate) { setDateResults([]); setSummary(null); return; }
    setLoadingDate(true);
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (currentEventId) params.set("event_id", currentEventId);
      const r = await api.get(`/checkin/by-date?${params.toString()}`);
      setDateResults(r.data.results); setSummary(r.data.summary);
    } finally { setLoadingDate(false); }
  };
  useEffect(() => { loadDate(); /* eslint-disable-line */ }, [selectedDate, currentEventId]);

  const search = async (val) => {
    setQ(val);
    if (val.length < 2) { setResults([]); return; }
    setBusy(true);
    try {
      const params = new URLSearchParams({ q: val });
      if (currentEventId) params.set("event_id", currentEventId);
      const r = await api.get(`/checkin/search?${params.toString()}`);
      setResults(r.data.results);
    } finally { setBusy(false); }
  };

  const searching = q.length >= 2;

  const checkin = async (id, force = false) => {
    const res = await doCheckin(id, { force });
    if (res.ok) {
      notify.success(res.warning ? `Checked in — note: visit is ${res.warning === "early" ? "tomorrow" : "yesterday"}` : "Checked in");
    } else if (res.reason === "date_warning") {
      const ok = await confirmDialog({
        title: "Date doesn't match today",
        description: `${res.attendee?.name}'s visit is booked for ${res.visit_date} (${res.warning}). Check in anyway?`,
        confirmLabel: "Check in anyway",
      });
      if (ok) return checkin(id, true);
      return;
    } else if (res.reason === "wrong_date") {
      notify.error(`Wrong date — this ticket is for ${res.visit_date}, not today (${res.today}).`);
    } else if (res.reason === "already_checked_in") {
      notify.error(`Already checked in at ${res.checked_in_at?.slice(11, 16)} (${res.checked_in_device || "?"}).`);
    } else {
      notify.error(REASON_MSG[res.reason] || "Could not check in.");
    }
    if (searching) await search(q); else await loadDate();
  };

  const OrderCard = (r) => (
    <div key={r.order.id} className="st-card p-4">
      <div className="flex justify-between items-start gap-3">
        <div>
          <div className="text-[#dc2626] font-bold">#{r.order.order_number}</div>
          <div className="text-sm">{r.order.customer_name} · {r.order.customer_email}</div>
          <div className="text-xs text-[#8a8a8a]">{r.event_date?.date ? formatOrdinalDate(r.event_date.date) : "—"} · {r.attendees.length} attendee{r.attendees.length === 1 ? "" : "s"}</div>
        </div>
        <button className="st-btn shrink-0" data-testid={`checkin-all-${r.order.id}`}
                onClick={async () => { for (const a of r.attendees) if (!a.checked_in && a.status !== "cancelled") await checkin(a.id); }}>
          <TicketIcon size={14} /> Check in all
        </button>
      </div>
      <div className="mt-4 space-y-2">
        {r.attendees.map((a) => (
          <div key={a.id} className="flex items-center justify-between bg-[#0e0e0e] border border-[#1a1a1a] rounded-lg p-3">
            <div>
              <div className="text-sm font-semibold">{a.name}</div>
              <div className="text-xs text-[#8a8a8a]">{a.ticket_type_name}</div>
            </div>
            {a.status === "cancelled" ? (
              <span className="st-chip" style={{ color: "#fca5a5" }} data-testid={`cancelled-${a.id}`}><Ban size={12} /> Cancelled</span>
            ) : a.checked_in ? (
              <span className="st-chip st-chip-amber" data-testid={`already-${a.id}`}>
                <AlertTriangle size={12} /> Already in @ {a.checked_in_at?.slice(11, 16)}
              </span>
            ) : (
              <button className="st-btn" onClick={() => checkin(a.id)} data-testid={`checkin-${a.id}`}>
                <CheckCircle2 size={14} /> Check in
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <DashboardLayout
      title="Check-in"
      subtitle="Pick a date to check in guests, or search by name / order number."
      actions={
        <div className="flex items-center gap-2">
          <Link to="/dashboard/checkin/scan" className="st-btn" data-testid="open-scan"><ScanLine size={14} /> Scan QR</Link>
          <Link to="/dashboard/checkin/kiosk" className="st-btn st-btn-ghost" data-testid="open-kiosk"><Maximize2 size={14} /> Kiosk mode</Link>
        </div>
      }
    >
      <div className="st-card p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-3 text-[#7a7a7a]" size={18} />
          <input autoFocus className="st-input text-lg py-3" style={{ paddingLeft: 44 }} placeholder="Search name, email, order #, or scan QR"
                 value={q} onChange={(e) => search(e.target.value)} data-testid="checkin-search" />
        </div>
        {busy && <div className="text-xs text-[#7a7a7a] mt-2">Searching…</div>}
      </div>

      {searching ? (
        <div className="space-y-4" data-testid="checkin-results">
          {results.length === 0 && !busy && (
            <div className="st-card p-8 text-center text-[#8a8a8a]">No matches in completed orders.</div>
          )}
          {results.map(OrderCard)}
        </div>
      ) : (
        <div className="grid lg:grid-cols-[300px_1fr] gap-4 items-start" data-testid="checkin-date-view">
          <div>
            <CompactCalendar
              dates={eventDates}
              mode="selection"
              selectedDate={selectedDate}
              onSelect={(d) => setSelectedDate(d.date)}
              testid="checkin-calendar"
            />
            {summary && (
              <div className="st-card p-3 mt-3 text-xs text-[#bbb]" data-testid="checkin-summary">
                <div className="flex justify-between"><span>To check in</span><span className="text-white font-semibold">{summary.to_check_in}</span></div>
                <div className="flex justify-between mt-1"><span>Checked in</span><span className="text-[#86efac]">{summary.checked_in}</span></div>
                <div className="flex justify-between mt-1 pt-1 border-t border-[#1a1a1a]"><span>Total guests</span><span>{summary.total}</span></div>
              </div>
            )}
          </div>
          <div className="space-y-4" data-testid="checkin-results">
            <div className="flex items-center gap-2 text-sm text-[#bbb]">
              <CalendarDays size={16} className="text-[#dc2626]" />
              <span data-testid="checkin-selected-date">{selectedDate ? formatOrdinalDate(selectedDate) : "No dates configured"}</span>
            </div>
            {loadingDate && <div className="st-card p-6 text-center text-[#7a7a7a]">Loading…</div>}
            {!loadingDate && dateResults.length === 0 && (
              <div className="st-card p-8 text-center text-[#8a8a8a]" data-testid="checkin-none">
                {selectedDate ? "No guests left to check in for this date." : "Select a date to see guests to check in."}
              </div>
            )}
            {!loadingDate && dateResults.map(OrderCard)}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

const KIOSK_STATES = {
  ok: { bg: "linear-gradient(135deg,#052e16,#14532d)", title: "Checked In", Icon: CheckCircle2 },
  warn_ok: { bg: "linear-gradient(135deg,#451a03,#78350f)", title: "Checked In — check date", Icon: AlertTriangle },
  already_checked_in: { bg: "linear-gradient(135deg,#451a03,#78350f)", title: "Already checked in", Icon: AlertTriangle },
  wrong_date: { bg: "linear-gradient(135deg,#450a0a,#7f1d1d)", title: "Wrong date", Icon: Clock },
  ticket_cancelled: { bg: "linear-gradient(135deg,#450a0a,#7f1d1d)", title: "Ticket cancelled", Icon: Ban },
  wrong_attraction: { bg: "linear-gradient(135deg,#450a0a,#7f1d1d)", title: "Wrong attraction", Icon: XCircle },
  order_not_confirmed: { bg: "linear-gradient(135deg,#450a0a,#7f1d1d)", title: "Not paid", Icon: XCircle },
  not_found: { bg: "linear-gradient(135deg,#450a0a,#7f1d1d)", title: "Ticket not found", Icon: XCircle },
};

export function KioskPage() {
  const [val, setVal] = useState("");
  const [state, setState] = useState(null); // { kind, sub }
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const v = val.trim(); if (!v) return;
    setVal("");
    try {
      const res = await doCheckin(v, { device: "kiosk", force: true });
      if (res.ok) {
        setState({ kind: res.warning ? "warn_ok" : "ok", sub: res.attendee?.name + (res.warning ? ` · visit ${res.attendee?.visit_date}` : "") });
      } else if (res.reason === "already_checked_in") {
        setState({ kind: "already_checked_in", sub: `${res.attendee?.name} at ${res.checked_in_at?.slice(11, 16)}` });
      } else if (res.reason === "wrong_date") {
        setState({ kind: "wrong_date", sub: `Ticket is for ${res.visit_date}, not today` });
      } else {
        setState({ kind: res.reason || "not_found", sub: res.attendee?.name || "Try again" });
      }
    } catch {
      setState({ kind: "not_found", sub: "Try again" });
    }
    setTimeout(() => { setState(null); inputRef.current?.focus(); }, 3500);
  };

  const exit = async () => {
    const pw = await promptDialog({
      title: "Exit kiosk mode", description: "Enter the admin password to exit.",
      promptLabel: "Password", promptType: "password", confirmLabel: "Exit", destructive: true,
    });
    if (pw === "exit" || pw === "kiosk") nav("/dashboard/checkin");
    else if (pw != null) await alertDialog({ title: "Wrong password", description: "Please try again." });
  };

  const cfg = state ? KIOSK_STATES[state.kind] : null;
  const bg = cfg?.bg || "#0a0a0a";
  const title = cfg?.title || "Ready to scan";
  const sub = state?.sub || "Show QR or type ticket ID";
  const Icon = cfg?.Icon || TicketIcon;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: bg, transition: "background .25s" }} data-testid={`kiosk-state-${state?.kind || "ready"}`}>
      <div className="flex justify-between p-6">
        <div className="st-brand text-white text-2xl">SCARE<span className="text-[#dc2626]">TICKETS</span></div>
        <button onClick={exit} className="st-btn st-btn-ghost" data-testid="kiosk-exit">Exit kiosk</button>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="text-center">
          <Icon size={120} className="mx-auto opacity-80 text-white" />
          <div className="text-6xl font-bold mt-6 text-white">{title}</div>
          <div className="text-2xl text-white/80 mt-2">{sub}</div>

          <form onSubmit={submit} className="mt-12">
            <input ref={inputRef} value={val} onChange={(e) => setVal(e.target.value)}
                   className="st-input text-center text-3xl py-5 px-6 max-w-xl mx-auto" style={{ background: "rgba(0,0,0,0.4)" }}
                   placeholder="Scan or type ticket ID then press Enter"
                   data-testid="kiosk-input" />
          </form>
        </div>
      </div>
    </div>
  );
}
